import { css, useMediaQuery, useTheme } from '@mui/material'
import { CalendarStatus } from '@prisma/client'
import { useTranslation } from 'next-i18next'
import Breadcrumb from 'components/Breadcrumb'
import { ButtonHeart } from 'components/ButtonHeart'
import VoteCard from 'components/VoteCard'
import { NewFlexContainer } from 'components/common/common.style'
import { Type } from 'components/event/RaceCard/RaceCard.style'
import {
  isLevel1Category,
  isLevel2Category,
  raceCategoriesLevel1,
  urlParser
} from 'helpers/raceCategoryLevels'
import { OpenContestEdition } from 'services/api/database/ContestService'
import { LiveEdition } from 'services/api/database/EventService'
import { Theme, themeUtils } from 'styles/theme'
import {
  BannerContainer,
  BannerTitle,
  ButtonHeartContainer
} from './map/Banner.style'

interface BannerProps {
  eventId: number
  mainRace?: LiveEdition['mainRace']
  slug: string
  name: string
  city: string
  countrySubdivisionDisplayCodeLevel2: string
  contest: OpenContestEdition | null
  isOver: boolean
  editionCalendarStatus: CalendarStatus
}

export default function Banner({
  mainRace,
  eventId,
  slug,
  name,
  city,
  countrySubdivisionDisplayCodeLevel2,
  contest,
  isOver,
  editionCalendarStatus
}: BannerProps) {
  const { t } = useTranslation('common')
  const { t: frenchT } = useTranslation('common', { lng: 'fr' })
  const theme: Theme = useTheme()
  const isMobile = useMediaQuery(themeUtils.maxWidth.sm)

  const mainType = mainRace?.categoryLevel1
  const mainDistance = mainRace?.categoryLevel2
  const typeLabel = mainType ? (t(mainType as never) as string) : ''
  const typeUrl = mainType
    ? raceCategoriesLevel1[mainType as keyof typeof raceCategoriesLevel1].url
    : ''
  const distanceLabel = mainDistance ? (t(mainDistance as never) as string) : ''
  const distanceUrl = urlParser(frenchT(mainDistance as never) as string)
  const isValidBreadcrumb =
    mainType &&
    isLevel1Category(mainType) &&
    mainDistance &&
    isLevel2Category(mainDistance)

  const showVoteCard =
    isMobile &&
    contest &&
    mainRace?.categoryLevel1 &&
    contest.raceTypes.includes(mainRace.categoryLevel1)

  return (
    <div
      css={css`
        height: auto;
        color: ${theme.palette.label.default};
        display: flex;
        flex-direction: column;
        position: relative;
        margin-top: 24px;
        ${themeUtils.maxWidth.md} {
          height: auto;
        }
      `}
    >
      {isValidBreadcrumb && (
        <div
          css={css`
            width: 100%;
            font-weight: 500;
            padding: 24px 0 24px 0;
            & a {
              color: ${theme.palette.label.default};
            }
            ${themeUtils.maxWidth?.md} {
              padding: 0 16px 16px;
            }
          `}
        >
          <Breadcrumb
            data={[
              {
                link: '/les-courses',
                label: t('eventPage.breadcrumb.allRaces')
              },
              { link: `/${typeUrl}`, label: typeLabel },
              { link: `/${distanceUrl}`, label: distanceLabel },
              { link: `/event/${slug}`, label: name }
            ]}
          />
        </div>
      )}
      <BannerContainer>
        <NewFlexContainer
          gap="15px"
          alignItems="center"
          css={css`
            flex: 1;
            ${themeUtils.maxWidth.md} {
              flex-direction: column-reverse;
              gap: 4px;
              align-items: flex-start;
            }
          `}
        >
          <BannerTitle>{name}</BannerTitle>
          {(editionCalendarStatus !== 'CONFIRMED' || isOver) && (
            <Type
              $color="onRed"
              $backgroundColor="red"
              css={css`
                width: fit-content;
              `}
            >
              {isOver
                ? t('event.footer.over')
                : editionCalendarStatus
                ? t(editionCalendarStatus)
                : ''}
            </Type>
          )}
        </NewFlexContainer>
        <ButtonHeartContainer>
          <ButtonHeart eventId={eventId} small={isMobile} />
        </ButtonHeartContainer>
      </BannerContainer>
      {showVoteCard && (
        <VoteCard
          key={eventId}
          event={{
            id: eventId,
            city,
            countrySubdivisionDisplayCodeLevel2,
            name
          }}
          badge={mainRace.categoryLevel1}
          contest={contest}
        />
      )}
    </div>
  )
}
